<template>
  <div class="app-root flex bg-gray-50 font-outfit dark:bg-gray-900">

    <!-- DESKTOP: SIDEBAR IZQUIERDO FIJO (logo + acciones) -->
    <div
      v-if="showChrome && !isMobile"
      class="flex-shrink-0 h-full bg-white dark:bg-gray-800 border-r-2 dark:border-gray-700 z-30"
    >
      <Header
        :sidebarCollapsed="false"
        @open-calculadora="mostrarCalculadora = !mostrarCalculadora"
      />
    </div>

    <!-- MÓVIL: OVERLAY -->
    <div
      v-if="drawerAbierto"
      class="fixed inset-0 bg-black/60 z-[200]"
      @click="drawerAbierto = false"
    ></div>

    <!-- MÓVIL: DRAWER LATERAL -->
    <aside v-if="drawerAbierto" class="drawer-mobile">
      <div class="drawer-mobile-header">
        <img
          :src="logoUrl"
          class="w-12 h-12 object-contain"
          alt="Logo"
          @error="handleLogoError"
        />
        <button @click="drawerAbierto = false" class="drawer-icon-btn">
          <Icon icon="mdi:close" width="24" height="24" />
        </button>
      </div>

      <div class="drawer-mobile-actions">
        <button @click="toggleCalculadoraMobile" class="drawer-icon-btn" title="Calculadora">
          <Icon icon="mdi:calculator" width="20" height="20" />
        </button>
        <button @click="fullscreenToggle" class="drawer-icon-btn" title="Pantalla completa">
          <Icon :icon="fullscreenMode ? 'mdi:fullscreen-exit' : 'mdi:fullscreen'" width="20" height="20" />
        </button>
        <button @click="toggleDarkMode" class="drawer-icon-btn" title="Modo oscuro">
          <Icon :icon="darkMode ? 'ri:moon-fill' : 'ph:sun-dim'" width="20" height="20" />
        </button>
      </div>

      <div class="drawer-mobile-bienvenida">
        <router-link
          to="/bienvenida"
          @click="cerrarDrawer"
          class="drawer-bienvenida-link"
        >
          <Icon icon="mdi:home-heart" width="18" height="18" />
          <span>Bienvenida</span>
        </router-link>
      </div>

      <!-- Zona con scroll del drawer -->
      <div class="drawer-mobile-scroll" ref="drawerScroll">
        <div class="drawer-mobile-content">
          <div v-if="loadingMenu" class="flex items-center gap-2 text-gray-400 text-xs px-3 py-2">
            <Icon icon="mdi:loading" class="animate-spin" width="16" height="16" />
            Cargando...
          </div>
          <div v-else-if="menuTreeSinBienvenida.length === 0" class="text-xs text-gray-400 italic px-3 py-2">
            Sin módulos
          </div>
          <template v-else>
            <MenuNode
              v-for="item in menuTreeSinBienvenida"
              :key="item.id"
              :item="item"
              :collapsed="false"
              :level="0"
              :active-section="activeSection"
              orientation="vertical"
              @item-click="cerrarDrawer"
              @section-opened="handleSectionOpened"
            />
          </template>
        </div>
      </div>

      <div class="drawer-mobile-footer">
        <button @click="handleLogout" class="drawer-logout-btn">
          <Icon icon="mdi:logout" width="18" height="18" />
          <span>Cerrar Sesión</span>
        </button>
      </div>
    </aside>

    <!-- ZONA PRINCIPAL -->
    <div class="main-zone">

      <!-- MÓVIL: TOPBAR -->
      <div v-if="showChrome && isMobile" class="mobile-topbar">
        <button @click="drawerAbierto = true" class="topbar-btn">
          <Icon icon="mdi:menu" width="24" height="24" />
        </button>

        <img :src="logoUrl" class="w-8 h-8 object-contain" alt="Logo" @error="handleLogoError" />

        <div class="flex-1"></div>

        <button @click="notifToggle" class="topbar-btn" style="position: relative;">
          <span
            v-if="totalNotificaciones > 0"
            class="absolute top-1 right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 border-2 border-white dark:border-gray-800 animate-pulseSoft"
          >
            {{ totalNotificaciones > 99 ? '99+' : totalNotificaciones }}
          </span>
          <Icon icon="mdi:bell-outline" width="22" height="22" />
        </button>

        <button @click="menuToggle" class="topbar-avatar-btn">
          <img
            v-if="userData.fotoPerfilUrl && !imageError"
            :src="userData.fotoPerfilUrl"
            class="w-9 h-9 rounded-full object-cover"
            alt="Foto"
            @error="handleMobileImageError"
          />
          <div
            v-else
            class="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold"
            :style="{ backgroundColor: avatarColor }"
          >
            {{ avatarInitials }}
          </div>
        </button>
      </div>

      <!-- DESKTOP: MENÚ HORIZONTAL -->
      <div v-if="showChrome && !isMobile" class="desktop-menu-bar">
        <SidebarMenu
          :collapsed="false"
          :menu-tree="menuTreeSinBienvenida"
          :loading="loadingMenu"
        />
      </div>

      <!-- CONTENIDO (único contenedor con scroll) -->
      <div class="content-scroll" id="body-scroll">
        <router-view v-slot="{ Component }">
          <transition name="slide-up" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
        <Footer v-if="showChrome" />
      </div>
    </div>
  </div>

  <CalculadoraFlotante :visible="mostrarCalculadora" @close="mostrarCalculadora = false" />
  <GlobalModal />

</template>

<script>
import { Icon } from "@iconify/vue";
import SidebarMenu from "@/components/Sidebar.vue";
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import MenuNode from "@/components/MenuNode.vue";
import GlobalModal from "@/components/common/GlobalModal.vue";
import AvisoCasheaSemanal from "@/components/AvisoCasheaSemanal.vue";
import AvisoServiciosPublicos from "@/components/AvisoServiciosPublicos.vue";
import CalculadoraFlotante from "@/components/CalculadoraFlotante.vue";
import { iniciarServicioNotificacionesPagos, detenerServicioNotificacionesPagos } from "@/utils/notificacionesPagosService.js";
import { apiFetch, getCurrentUser, isAuthenticated } from "@/api/index.js";
import { obtenerLogo } from "@/utils/tenantConfig";
import { fullscreen } from "@/helper/fullscreen";
import { setDarkMode, loadDarkMode } from "@/helper/theme";
import {
  cargarNotificaciones, contarNoLeidas, marcarLeida,
  limpiarNotificaciones, tiempoRelativo, verificarSuperAdmin
} from '@/utils/notificaciones';

export default {
  name: "App",
  components: {
    Icon, Header, Footer, SidebarMenu, MenuNode,
    GlobalModal, AvisoCasheaSemanal, AvisoServiciosPublicos,
    CalculadoraFlotante,
  },
  data() {
    return {
      pagosPendientesCount: 0,
      sidebar: false,
      sidebarCollapsed: false,
      mostrarCalculadora: false,
      drawerAbierto: false,
      isMobile: false,
      menuTree: [],
      activeSection: null,
      loadingMenu: true,
      menu: false,
      darkMode: false,
      notification: false,
      fullscreenMode: false,
      logoError: false,
      tenantConfigVersion: 0,
      haySesion: false,
      userData: {
        idUsuario: null, nombre: '', apellido: '', email: '',
        nombreRol: '', fotoPerfilUrl: null,
      },
      notifList: [],
      notifNoLeidas: 0,
      imageError: false,
    };
  },
  computed: {
    logoUrl() {
      const _v = this.tenantConfigVersion;
      return obtenerLogo();
    },
    avatarInitials() {
      const n = this.userData.nombre || '';
      const a = this.userData.apellido || '';
      return ((n.charAt(0) + a.charAt(0)).toUpperCase()) || 'U';
    },
    avatarColor() {
      const colors = ['#4F46E5','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#6366F1'];
      let hash = 0;
      const str = this.avatarInitials;
      for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
      return colors[Math.abs(hash) % colors.length];
    },
    showChrome() {
      const meta = this.$route.meta || {};
      if (meta.hideNav) return false;
      if (meta.publicPage) return false;
      return true;
    },
    menuTreeSinBienvenida() {
      return (this.menuTree || []).filter(item => {
        if (!item) return false;
        const n = (item.nombre || '').toLowerCase().trim();
        return !n.includes('bienvenid');
      });
    },
  },
  watch: {
    $route() {
      this.sidebar = false;
      this.drawerAbierto = false;
      this.menu = false;
      this.notification = false;
    },
    isMobile(newVal) {
      if (!newVal) this.drawerAbierto = false;
    },
    // ✅ Ya NO tocamos document.body.style.overflow aquí.
    // El body no scrollea (el scroll vive en .content-scroll),
    // y el overlay + drawer ya cubren la pantalla.
  },
  async mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);

    // ✅ ELIMINADO: los 4 document.*.style.overflow/height = 'hidden'/'100%'
    // Eso lo maneja el CSS de abajo, una sola vez y sin pelear con el JS.

    this.haySesion = isAuthenticated();
    this.loadSidebarPreference();
    await this.loadMenu();
    this.loadUserData();
    this.darkMode = this.loadDarkMode();

    window.addEventListener('userUpdated', this.loadUserData);
    window.addEventListener('userUpdated', this.loadMenu);
    window.addEventListener('menuUpdated', this.loadMenu);
    window.addEventListener('userLoggedIn', this.onUserLoggedIn);
    window.addEventListener('userLoggedOut', this.onUserLoggedOut);
    window.addEventListener('auth-change', this.onAuthChange);
    window.addEventListener('tenantConfigUpdated', this.refrescarTenantConfig);

    this.actualizarNotificaciones();
    iniciarServicioNotificacionesPagos(this.$toast, this.$router);
    this._onPagosPendientes = (e) => { this.pagosPendientesCount = e.detail?.cantidad || 0; };
    window.addEventListener('pagosPendientesActualizados', this._onPagosPendientes);
    this._notifInterval = setInterval(() => this.actualizarNotificaciones(), 30000);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
    window.removeEventListener('userUpdated', this.loadUserData);
    window.removeEventListener('userUpdated', this.loadMenu);
    window.removeEventListener('menuUpdated', this.loadMenu);
    window.removeEventListener('userLoggedIn', this.onUserLoggedIn);
    window.removeEventListener('userLoggedOut', this.onUserLoggedOut);
    window.removeEventListener('auth-change', this.onAuthChange);
    window.removeEventListener('tenantConfigUpdated', this.refrescarTenantConfig);
    if (this._onPagosPendientes) window.removeEventListener('pagosPendientesActualizados', this._onPagosPendientes);
    detenerServicioNotificacionesPagos();
    if (this._notifInterval) clearInterval(this._notifInterval);
  },
  methods: {
    fullscreen, setDarkMode, loadDarkMode, tiempoRelativo,

    onUserLoggedIn() {
      this.haySesion = true;
      this.loadUserData();
      this.loadMenu();
    },
    onUserLoggedOut() {
      this.haySesion = false;
      this.menuTree = [];
      this.userData = { idUsuario: null, nombre: '', apellido: '', email: '', nombreRol: '', fotoPerfilUrl: null };
    },
    onAuthChange(e) {
      const autenticado = e.detail?.authenticated === true;
      this.haySesion = autenticado;
      if (autenticado) {
        this.loadUserData();
        this.loadMenu();
      }
    },
    checkMobile() {
      this.isMobile = window.matchMedia('(max-width: 1023px)').matches;
      if (!this.isMobile) this.drawerAbierto = false;
    },
    toggleDarkMode() {
      this.darkMode = !this.darkMode;
      this.setDarkMode(this.darkMode);
    },
    toggleCalculadoraMobile() {
      this.mostrarCalculadora = !this.mostrarCalculadora;
      this.drawerAbierto = false;
    },
    fullscreenToggle() {
      this.fullscreenMode = !this.fullscreenMode;
      this.fullscreen(this.fullscreenMode);
    },
    cerrarDrawer() {
      this.drawerAbierto = false;
    },
    handleSectionOpened(sectionName) { this.activeSection = sectionName; },

    async loadMenu() {
      this.loadingMenu = true;
      try {
        const user = getCurrentUser();
        if (!user) { this.menuTree = []; return; }
        const roleId = user.idRol ?? user.id_rol ?? user.rol?.idRol ?? user.rolId;
        if (!roleId) { this.menuTree = []; return; }
        const response = await apiFetch(`/Menu/usuario/${roleId}`);
        const tree = this.reordenarMenu(this.normalizarArbol(response));
        this.menuTree = Array.isArray(tree) ? tree.filter(Boolean) : [];
      } catch (error) {
        console.error('❌ Error menú:', error);
        this.menuTree = [];
      } finally {
        this.loadingMenu = false;
      }
    },
    normalizarArbol(response) {
      let lista = response;
      if (response?.data && Array.isArray(response.data)) lista = response.data;
      if (!Array.isArray(lista)) return [];
      return lista.map(this.normalizarItem).filter(Boolean);
    },
    normalizarItem(item) {
      if (!item || typeof item !== 'object') return null;
      const hijosRaw = item.hijos ?? item.Hijos ?? item.children ?? [];
      const hijos = Array.isArray(hijosRaw)
        ? hijosRaw.map(this.normalizarItem).filter(Boolean)
            .sort((a, b) => (a.orden ?? 999) - (b.orden ?? 999))
        : [];
      return {
        id:      item.id ?? item.Id ?? item.idModulo,
        idPadre: item.idPadre ?? item.IdPadre ?? null,
        nombre:  item.nombre ?? item.Nombre ?? '',
        icono:   item.icono ?? item.Icono ?? null,
        ruta:    item.ruta ?? item.Ruta ?? null,
        tipo:    item.tipo ?? item.Tipo ?? 'modulo',
        orden:   item.orden ?? item.Orden ?? 999,
        hijos
      };
    },
    reordenarMenu(tree) {
      if (!Array.isArray(tree)) return [];
      const sortRecursivo = (nodos) => {
        return [...nodos].filter(Boolean)
          .sort((a, b) => (a.orden ?? 999) - (b.orden ?? 999))
          .map(n => ({ ...n, hijos: sortRecursivo(n.hijos || []) }));
      };
      const ordenado = sortRecursivo(tree);
      const PANEL_KEYWORDS = ['panel de gestión', 'panel de gestion'];
      const panelIdx = ordenado.findIndex(item => {
        const n = (item.nombre || '').toLowerCase().trim();
        return PANEL_KEYWORDS.some(k => n.includes(k));
      });
      if (panelIdx > 0) {
        const [panel] = ordenado.splice(panelIdx, 1);
        ordenado.unshift(panel);
      }
      return ordenado;
    },
    menuToggle() { this.menu = !this.menu; },
    notifToggle() {
      this.notification = !this.notification;
      if (this.notification) this.actualizarNotificaciones();
    },
    marcarLeida(id) { marcarLeida(id); this.actualizarNotificaciones(); },
    limpiarNotificaciones() {
      if (confirm('¿Limpiar todas las notificaciones?')) {
        limpiarNotificaciones(); this.actualizarNotificaciones();
      }
    },
    actualizarNotificaciones() {
      if (!verificarSuperAdmin()) return;
      this.notifList = cargarNotificaciones();
      this.notifNoLeidas = contarNoLeidas();
    },
    verTodasNotificaciones() { this.notification = false; this.$router.push('/notificaciones'); },
    async loadUserData() {
      try {
        const s = localStorage.getItem('user') || localStorage.getItem('user_data');
        if (!s) return;
        const u = JSON.parse(s);
        this.userData = {
          idUsuario: u.idUsuario || u.id,
          nombre: u.nombre || '',
          apellido: u.apellido || '',
          email: u.email || '',
          nombreRol: u.nombreRol || u.rol?.nombre || 'Usuario',
          fotoPerfilUrl: u.fotoPerfilUrl || null,
        };
      } catch (e) { console.error(e); }
    },
    handleLogout() {
      if (confirm('¿Cerrar sesión?')) {
        ['user','user_data','token','userPermissions','isAuthenticated','rememberedUsername','sidebarCollapsed']
          .forEach(k => localStorage.removeItem(k));
        window.dispatchEvent(new CustomEvent('userLoggedOut'));
        this.$router.push('/login');
      }
    },
    handleLogoError() { this.logoError = true; },
    handleMobileImageError() { this.imageError = true; this.userData.fotoPerfilUrl = null; },
    refrescarTenantConfig() { this.tenantConfigVersion++; },
    loadSidebarPreference() {
      const saved = localStorage.getItem('sidebarCollapsed');
      if (saved !== null) this.sidebarCollapsed = saved === 'true';
    },
  },
};
</script>

<style>
input[type="password"] {
  text-transform: none !important;
}

.slide-up-enter-active { transition: all 0.3s ease-out; }
.slide-up-leave-active { transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1); }
.slide-up-enter-from, .slide-up-leave-to { transform: translateY(20px); opacity: 0; }

/* ============================================================
   BASE: html/body/#app ocupan el alto de la pantalla.
   NO ponemos overflow:hidden aquí. El layout (.main-zone con
   altura fija + .content-scroll con overflow-y:auto) es quien
   controla el scroll. Así el navegador móvil no se confunde.
   ============================================================ */
html,
body,
#app {
  margin: 0;
  padding: 0;
  height: 100%;
}

/* Evita el "rebote"/pull-to-refresh del documento en Android */
body {
  overscroll-behavior-y: none;
}

/* ============================================================
   vue-toastification: el container NO debe bloquear el touch
   (esto reemplaza todo el hack de JS + MutationObserver)
   ============================================================ */
.Vue-Toastification__container {
  pointer-events: none !important;
}
.Vue-Toastification__toast {
  pointer-events: auto !important;
}

/* ============================================================
   LAYOUT RAÍZ
   ============================================================ */
.app-root {
  height: 100vh;   /* fallback */
  height: 100dvh;
  overflow: hidden;
}

/* ============================================================
   ZONA PRINCIPAL (columna: topbar/menú + contenido scrolleable)
   ============================================================ */
.main-zone {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

/* ============================================================
   CONTENIDO PRINCIPAL — único scroll de la app
   ============================================================ */
.content-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  position: relative;
  z-index: 0;
  padding-bottom: 40px;
  /* ✅ sin touch-action, sin height:auto, sin max-height */
}

/* ============================================================
   DRAWER MÓVIL
   ============================================================ */
.drawer-mobile {
  position: fixed;
  top: 0;
  left: 0;
  width: 280px;
  max-width: 85vw;
  height: 100vh;
  height: 100dvh;
  background: white;
  z-index: 210;
  display: flex;
  flex-direction: column;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15);
}

.dark .drawer-mobile {
  background: #1f2937;
}

.drawer-mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.dark .drawer-mobile-header { border-bottom-color: #374151; }

.drawer-mobile-actions {
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.dark .drawer-mobile-actions { border-bottom-color: #374151; }

.drawer-mobile-bienvenida {
  padding: 8px;
  flex-shrink: 0;
}

.drawer-bienvenida-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #7c3aed;
  background: #f5f3ff;
  text-decoration: none;
}
.dark .drawer-bienvenida-link {
  color: #c4b5fd;
  background: rgba(124, 58, 237, 0.15);
}

/* Scroll del drawer */
.drawer-mobile-scroll {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain; /* evita que el scroll del drawer "pase" al fondo */
  -webkit-overflow-scrolling: touch;
}

.drawer-mobile-content {
  padding: 8px;
}

.drawer-mobile-footer {
  padding: 12px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.dark .drawer-mobile-footer { border-top-color: #374151; }

.drawer-icon-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  color: #6b7280;
  background: transparent;
  border: none;
  cursor: pointer;
}
.drawer-icon-btn:hover { background: #f3f4f6; }
.dark .drawer-icon-btn:hover { background: #374151; }

.drawer-logout-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  color: #dc2626;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
}
.drawer-logout-btn:hover { background: #fef2f2; }

/* ============================================================
   TOPBAR MÓVIL
   ============================================================ */
.mobile-topbar {
  flex-shrink: 0;
  background: white;
  border-bottom: 2px solid #e5e7eb;
  z-index: 20;
  height: 56px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  gap: 12px;
}
.dark .mobile-topbar {
  background: #1f2937;
  border-bottom-color: #374151;
}

.topbar-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  color: #6b7280;
  background: transparent;
  border: none;
  cursor: pointer;
}
.topbar-btn:hover { background: #f3f4f6; }

.topbar-avatar-btn {
  border-radius: 9999px;
  padding: 0;
  background: transparent;
  border: 2px solid #e5e7eb;
  cursor: pointer;
}

/* ============================================================
   MENÚ DESKTOP HORIZONTAL
   ============================================================ */
.desktop-menu-bar {
  flex-shrink: 0;
  background: white;
  border-bottom: 2px solid #e5e7eb;
  position: relative;
  z-index: 500;
  isolation: isolate;
}
.dark .desktop-menu-bar {
  background: #1f2937;
  border-bottom-color: #374151;
}

/* ============================================================
   SCROLLBARS
   ============================================================ */
.drawer-mobile-scroll::-webkit-scrollbar,
.content-scroll::-webkit-scrollbar {
  width: 6px;
}
.drawer-mobile-scroll::-webkit-scrollbar-track,
.content-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.drawer-mobile-scroll::-webkit-scrollbar-thumb,
.content-scroll::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.4);
  border-radius: 3px;
}
.drawer-mobile-scroll::-webkit-scrollbar-thumb:hover,
.content-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(148, 163, 184, 0.6);
}
</style>